// ChatComponents.js

import React from 'react';
import { 
  Target, Search, CheckCircle, Clock, Sparkles, List, ArrowRight,
  ChevronLeft, ChevronRight, Zap, Globe, FileText, ExternalLink, X
} from 'lucide-react';

// Sélecteur d'étape rapide
export const StepSelector = ({ actionPlan, currentStepId, onStepChange }) => {
  if (!actionPlan?.steps) return null;

  return (
    <div className="bg-white rounded-lg border border-orange-200 p-3 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Target size={16} className="text-orange-600" />
        <span className="text-sm font-medium text-gray-700">Étape actuelle :</span>
      </div>
      <select
        value={currentStepId || ''}
        onChange={(e) => onStepChange(parseInt(e.target.value))}
        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
      >
        <option value="">Choisir une étape...</option>
        {actionPlan.steps.map((step) => (
          <option key={step.id} value={step.id}>
            {step.termine ? '✅' : '🔄'} Étape {step.id}: {step.titre}
          </option>
        ))}
      </select>
    </div>
  );
};

// Affichage de l'étape courante
export const CurrentStepDisplay = ({ 
  actionPlan, 
  currentStepId, 
  onStepComplete, 
  onStepChange,
  isCompact = false,
  forceUpdate = 0 
}) => {
  if (!actionPlan?.steps || !currentStepId) return null;

  const currentStep = actionPlan.steps.find(step => step.id === currentStepId);
  if (!currentStep) return null;

  const stepIndex = actionPlan.steps.findIndex(step => step.id === currentStepId);
  const nextStep = actionPlan.steps.find(step => !step.termine && step.id !== currentStepId);

  return (
    <div 
      key={`step-${currentStepId}-${currentStep.termine}-${forceUpdate}`}
      className={`bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-200 ${
      isCompact ? 'p-4' : 'p-6'
    }`}>
      {/* En-tête de l'étape */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
            currentStep.termine 
              ? 'bg-green-500 text-white' 
              : 'bg-orange-500 text-white'
          }`}>
            {currentStep.termine ? '✓' : currentStep.id}
          </div>
          <div>
            <h3 className={`font-semibold ${
              currentStep.termine ? 'text-green-700 line-through' : 'text-orange-700'
            } ${isCompact ? 'text-sm' : 'text-lg'}`}>
              {currentStep.titre}
            </h3>
            <p className="text-xs text-gray-600">
              Étape {stepIndex + 1} sur {actionPlan.steps.length}
            </p>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex items-center gap-2">
          {!currentStep.termine && (
            <button
              onClick={() => onStepComplete(currentStepId)}
              className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-1"
            >
              <CheckCircle size={14} />
              Terminer
            </button>
          )}
          
          {currentStep.termine && nextStep && (
            <button
              onClick={() => onStepChange(nextStep.id)}
              className="px-3 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm flex items-center gap-1"
            >
              <ArrowRight size={14} />
              Suivante
            </button>
          )}
        </div>
      </div>

      {/* Description de l'étape */}
      <div className="mb-4">
        <p className={`${isCompact ? 'text-sm' : 'text-base'} ${
          currentStep.termine ? 'text-green-600' : 'text-gray-700'
        } leading-relaxed`}>
          {currentStep.description}
        </p>
      </div>

      {/* Informations supplémentaires */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          {currentStep.duree && (
            <span className="flex items-center gap-1 text-gray-600">
              <Clock size={14} />
              {currentStep.duree}
            </span>
          )}
          <span className={`flex items-center gap-1 ${
            currentStep.termine ? 'text-green-600' : 'text-orange-600'
          }`}>
            <Zap size={14} />
            {currentStep.termine ? 'Terminée' : 'En cours'}
          </span>
        </div>

        {/* Navigation entre étapes */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const prevStep = actionPlan.steps[stepIndex - 1];
              if (prevStep) onStepChange(prevStep.id);
            }}
            disabled={stepIndex === 0}
            className="p-1 rounded hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Étape précédente"
          >
            <ChevronLeft size={16} className="text-orange-600" />
          </button>
          
          <span className="text-xs text-gray-500">
            {stepIndex + 1}/{actionPlan.steps.length}
          </span>
          
          <button
            onClick={() => {
              const nextStep = actionPlan.steps[stepIndex + 1];
              if (nextStep) onStepChange(nextStep.id);
            }}
            disabled={stepIndex === actionPlan.steps.length - 1}
            className="p-1 rounded hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Étape suivante"
          >
            <ChevronRight size={16} className="text-orange-600" />
          </button>
        </div>
      </div>

      {/* Message de félicitations si étape terminée */}
      {currentStep.termine && (
        <div className="mt-4 p-3 bg-green-100 rounded-lg border border-green-200">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-green-600" />
            <span className="text-sm font-medium text-green-700">
              Félicitations ! Étape terminée avec succès.
            </span>
          </div>
          {nextStep && (
            <p className="text-xs text-green-600 mt-1">
              Prochaine étape : {nextStep.titre}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Vue d'ensemble compacte du plan
export const PlanOverview = ({ actionPlan, currentStepId, onStepSelect, forceUpdate = 0 }) => {
  if (!actionPlan?.steps) return null;

  const completedCount = actionPlan.steps.filter(s => s.termine).length;
  const progressPercent = Math.round((completedCount / actionPlan.steps.length) * 100);

  return (
    <div key={`plan-overview-${forceUpdate}`} className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <List size={16} className="text-blue-600" />
          <span className="font-medium text-gray-700">Plan d'action</span>
        </div>
        <span className="text-xs text-gray-500">
          {completedCount}/{actionPlan.steps.length} terminées ({progressPercent}%)
        </span>
      </div>

      {/* Barre de progression */}
      <div className="mb-4">
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-orange-400 to-green-500 transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Liste compacte des étapes */}
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {actionPlan.steps.map((step) => (
          <button
            key={`step-btn-${step.id}-${step.termine}`}
            onClick={() => onStepSelect(step.id)}
            className={`w-full text-left p-2 rounded-lg transition-colors ${
              step.id === currentStepId
                ? 'bg-orange-100 border border-orange-300'
                : step.termine
                ? 'bg-green-50 hover:bg-green-100'
                : 'bg-gray-50 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold ${
                step.termine
                  ? 'bg-green-500 text-white'
                  : step.id === currentStepId
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}>
                {step.termine ? '✓' : step.id}
              </div>
              <span className={`text-sm flex-1 ${
                step.termine ? 'text-green-700 line-through' : 'text-gray-700'
              }`}>
                {step.titre}
              </span>
              {step.id === currentStepId && (
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Modal Sources
export const SourcesModal = ({ show, onClose, savedSources }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  
  const filteredSources = savedSources.filter(source =>
    source.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    source.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    source.domain.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-y-auto">
        
        {/* En-tête */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 flex items-center justify-center">
                <Globe size={16} className="text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Sources Sélectionnées</h3>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                {savedSources.length} sources
              </span>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Barre de recherche */}
        {savedSources.length > 0 && (
          <div className="sticky top-[73px] bg-white px-6 py-4 border-b border-gray-100 z-10">
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher dans les sources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        )}
        
        {/* Contenu principal */}
        <div className="px-6 py-4">
          {savedSources.length > 0 ? (
            <>
              {filteredSources.length > 0 ? (
                <div className="space-y-4">
                  {filteredSources.map((source, index) => (
                    <a
                      key={index}
                      href={source.url}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-5 border border-green-200 hover:border-green-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-6 h-6 rounded bg-green-100 flex items-center justify-center flex-shrink-0">
                              <FileText size={14} className="text-green-600" />
                            </div>
                            <h4 className="font-semibold text-gray-800 group-hover:text-green-700 transition-colors truncate">
                              {source.title}
                            </h4>
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {source.description}
                          </p>
                          
                          <div className="flex flex-wrap items-center gap-3 text-sm">
                            <span className="flex items-center gap-1 text-gray-500">
                              <Globe size={12} />
                              <span className="truncate max-w-[200px]">{source.domain}</span>
                            </span>
                            <span className="px-2 py-1 bg-white bg-opacity-80 border border-green-200 rounded-full text-xs font-medium text-green-700">
                              {source.type}
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              Fiabilité: {source.reliability}
                            </span>
                            {source.relevanceScore && (
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                Score: {source.relevanceScore}/10
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-center gap-2 flex-shrink-0">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <ExternalLink size={18} className="text-green-600" />
                          </div>
                          <div className="text-xs text-gray-400 font-medium">
                            #{index + 1}
                          </div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search size={48} className="mx-auto text-gray-300 mb-4" />
                  <h4 className="text-lg font-semibold text-gray-600 mb-2">Aucun résultat trouvé</h4>
                  <p className="text-sm text-gray-500">
                    Essayez avec d'autres mots-clés.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <Globe size={48} className="mx-auto text-gray-300 mb-4" />
              <h4 className="text-lg font-semibold text-gray-600 mb-2">Aucune source disponible</h4>
              <p className="text-sm text-gray-500">
                Recherchez et sauvegardez des sources pour enrichir vos conversations avec Aladinn.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
